import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@MasterBluePrint/Overrides/Components/ProductContextAvailability"; 
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
// import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductAddToCartButton from "@MasterBluePrint/Overrides/Components/ProductAddToCartButton";
import ProductDescription, { ProductDescriptionStyles } from "@MasterBluePrint/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@MasterBluePrint/Overrides/Components/ProductImage";
import ProductPrice from "@MasterBluePrint/Overrides/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@MasterBluePrint/Overrides/Components/ProductQuantityBreakPricing";
import themeColors from "@MasterBluePrint/Styles/variables";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";


const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    state,
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showBreakPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    carouselType: string;
    index: number;
    relatedProductType: string;
    hideProductPriceSavingsMessage: boolean;
    carouselTitle: string;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productMfg?: InjectableCss;
    productMfgContainer?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: .3750625rem 0;
            min-height: 1.375rem;
            color: ${themeColors.colorPrimary};
            font-size: .9375rem;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 1.5rem;
        `,
    },
    productMfg: {
        css: css`
        display: flex;
        color: ${themeColors.colorBrandsSeeMore};
        font-family: Lato;
        font-size: .875rem;
        line-height: 1.5rem;
        font-weight: bold;
        `,
    },
    productMfgContainer: {
        css: css`
        margin-bottom: .3750625rem;
        color: #333F48;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: .3750625rem;
                span {
                    color: ${themeColors.colorBrandsSeeMore} !important;
                    font-family: Lato;
                    font-size: .9375rem;
                    font-weight: bold;
                    line-height: 1.5rem;
                    margin-bottom: 0;
                    min-height: 4.5rem;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;  
                    overflow: hidden;
                    @media screen and (max-width: 1199px) and (min-width: 992px) {
                        min-height: 4.5rem;
                    }
                }
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: .3750625rem;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: inherit;
            margin-bottom: .3750625rem;
            line-height: 1.4375rem;

            span[data-test-selector="productPrice_unitNetPrice"] {
                font-family: 'Lato';
                font-weight: normal;

                @media screen and (min-width: 768px) and (max-width: 1200px) {
                    font-size: 1.125rem !important;
                }
                @media screen and (min-width: 1201px) and (max-width: 1250px) {
                    font-size: .9375rem !important;
                }
                @media screen and (min-width: 1250px) and (max-width: 1300px) {
                    font-size: 1rem !important;
                }
                @media screen and (min-width: 1301px) and (max-width: 1400px) {
                    font-size: 1rem !important;
                }
                @media screen and (min-width: 1401px) {
                    font-size: 1.3125rem !important;
                }
            }
            span[data-test-selector="productPrice_unitOfMeasureLabel"] {
                margin-right: 0;

                @media screen and (min-width: 768px) and (max-width: 1200px) {
                    font-size: .8125rem !important;
                    margin-left: 0.31250625rem;
                }
                @media screen and (min-width: 1201px) and (max-width: 1300px) {
                    font-size: .8125rem !important;
                    margin-left: 0.31250625rem;
                }
                @media screen and (min-width: 1301px) and (max-width: 1400px) {
                    font-size: .8125rem !important;
                    margin-left: 0.31250625rem;
                }
            }
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: .3750625rem;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: .3750625rem;

            &.cart-btn-wrapper {
                > div > div {
                    flex-basis: 100% !important;
                    max-width: 100% !important;
                }
            }

            button {
                margin-top: 1.5rem !important;
                margin-bottom: 0 !important;
            }
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: {
        width: [9],
        css: css`
        flex-basis: 80% !important;
        max-width: 80% !important;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                    flex-basis: 90% !important;
                    max-width: 90% !important;
                `,
                        css`
                    flex-basis: 100%;
                    max-width: 100%;
                `,
                    ],
                    "max",
                )};
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            border: 1px solid ${themeColors.colorPrimary};
            border-radius: .125rem;
            background-color: ${themeColors.colorWhite};
            width: 100%;
            span {
                font-size: .9375rem;
                letter-spacing: 0;
                line-height: 1.3125rem;
                text-align: center;
                font-weight: normal;
            }

            &:hover {
                span {
                    font-weight: bold;
                }
            }
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: .75rem;
            `,
        },
    },
};

const AttributeCarouselProducts: React.FC<Props> = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showBreakPrice,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    carouselType,
    state,
    index,
    relatedProductType,
    hideProductPriceSavingsMessage,
    carouselTitle
}) => {
    const [styles] = React.useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext: ProductContextModel = {
        product,
        productInfo,
        idx: index,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const allAttrTypes = productContext?.product?.attributeTypes;
    const filteredAllAttrTypes = allAttrTypes?.filter(item => item.includeOnProduct === true);
    const attrTypes = filteredAllAttrTypes?.slice(0, 3);

    return (
        <Typography as="div" className={`${showAddToCart ? 'btn-present' : 'btn-not-present'}`} >
            <ProductContext.Provider value={productContext}>
                {showImage && (
                    <ProductImage
                        carouselType={carouselType}
                        product={productContext}
                        extendedStyles={styles.productImageStyles}
                        data-test-selector="carousal-img"
                        carouselTitle={carouselTitle}
                    />
                )}
                {showBrand && product.brand && (
                    <StyledWrapper {...styles.productBrandWrapper} className="carousal-brands">
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                )}
                {showTitle && (
                    <ProductDescription
                        carouselType={carouselType}
                        product={productContext}
                        extendedStyles={styles.productDescriptionStyles}
                        index={index}
                        carouselTitle={carouselTitle}
                        data-test-selector="carousal-description"
                    />
                )}
                {showPartNumbers && (
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        showCustomerName={false}
                        showManufacturerItem={false}
                        extendedStyles={styles.productPartNumbersStyles}
                        data-test-selector="carousal-part-number"
                    />
                )}
                <StyledWrapper className="attribute-wrapper">
                    <StyledWrapper {...styles.productMfgContainer} className="mfg-container">
                        <Typography {...styles.productMfg}> {translate("Mfg Part")}</Typography>
                        <Typography> {productContext.product.manufacturerItem}</Typography>
                    </StyledWrapper>
                    {attrTypes && attrTypes.map((item) => (
                        item.attributeValues?.[0]?.value && (
                            <StyledWrapper key={item?.id} {...styles.productMfgContainer} className="new-attr attr-analog-channel">
                                <Typography {...styles.productMfg}>{item.name}:</Typography>
                                <Typography>{item.attributeValues[0].value}</Typography>
                            </StyledWrapper>
                        )
                    ))}

                </StyledWrapper>

                {showPrice && (
                    <StyledWrapper {...styles.productPriceWrapper} data-test-selector="carousal-price-wrapper">
                        <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                            {translate("U/M")}
                        </VisuallyHidden>
                        <ProductPrice showLabel={false} product={productContext} isPartOfCarousel={true} hideProductPriceSavingsMessage={hideProductPriceSavingsMessage} />
                        {product.unitOfMeasures && (
                            <ProductUnitOfMeasureSelect
                                labelOverride=""
                                extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                id={`${carouselId}-uom`}
                            />
                        )}
                        {showBreakPrice && (<ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />)}
                    </StyledWrapper>
                )}

                <ProductContextAvailability data-test-selector="carousal-prod-availability" isCarouselProduct={true} />
                {showAddToCart && (
                    <GridContainer {...styles.addToCartContainer} className="cart-btn-wrapper">
                        {/* <GridItem {...styles.qtyGridItem}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride=""
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${carouselId}-qty`}
                            />
                        </GridItem> */}
                        <GridItem {...styles.addToCartButtonGridItem}>
                            <ProductAddToCartButton
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                                indexCarouselPosition={index}
                                pageSource={carouselType}
                            />
                        </GridItem>
                    </GridContainer>
                )}
                {showAddToList && (
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                )}
            </ProductContext.Provider>
        </Typography>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AttributeCarouselProducts);
